import {Component, OnInit, OnDestroy} from '@angular/core';
import {ChartDataSet, ChartOptions} from '../../../../../shared/components/chart/chart.component';
import {Router} from '@angular/router';
import {ChartsVccService} from '../../../../service/charts-vcc.service';
import moment from 'moment';
import {VccChartFilter} from '../../../../model/funding-vcc-chart-filter';
import {MatchingResult} from '../../../../model/matching-result';
import {TooltipItem} from 'chart.js';
import {MatchingVccService} from 'src/app/finance-resource/service/matching-vcc.service';
import {ChartResponse} from '../../../../model/vcc-chart-model';
import {MatchedVccFilter} from 'src/app/finance-resource/model/match-vcc';
import {MerchantsService} from 'src/infrastructure/merchants/service/merchants.service';
import {Merchant} from 'src/app/dashboard/model/merchant-guid';
import {finalize, catchError, takeUntil} from 'rxjs/operators';
import {Subject, of} from 'rxjs';

@Component({
    selector: 'matching-vcc-chart',
    templateUrl: './matching-vcc-chart.component.html',
    standalone: false
})

export class MatchingVccChartComponent implements OnInit, OnDestroy {

  public shortLabels: string[] = [];
  merchantGuids: Merchant[] = [];
  public vccChartFilter: VccChartFilter = {} as VccChartFilter;
  public dataMatching!: ChartDataSet;
  originalMatch: number[] = [];
  originalUnmatch: number[] = [];
  limitPercentageMatch: number = 0;
  limitPercentageUnmatch: number = 0;
  baseLimit: number = 0.11;
  adjustedValueMatch: number = 0;
  adjustedValueUnmatch: number = 0;
  adjustedLimit: number = 0.5;
  public matchingData: MatchingResult[] = [];
  public matchingVccFilterConfigResult: MatchedVccFilter = {};
  public isLoading: boolean = true;
  public isError: boolean = false;

  private destroy$ = new Subject<void>();
  public matchingVccChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      tooltip: {
          callbacks: {
              label: (tooltipItem: TooltipItem<'bar'>) => {
                const originalMatch = this.originalMatch[tooltipItem.dataIndex];
                const originalUnmatch = this.originalUnmatch[tooltipItem.dataIndex];
                if (tooltipItem.dataset.label === 'Match') {
                  return `${tooltipItem.label}: ${originalMatch}`;
                } else {
                  return`${tooltipItem.label}: ${originalUnmatch}`;
                }
              }
          }
      },
        legend: {
          display: true,
          align: 'center'
        },
        datalabels: {
          color: '#FFFFFF',
          align: 'start',
          anchor: 'end',
          borderWidth: 1,
          borderRadius: 5,
          offset: 1,
          font: {
            weight: 'bold'
          },
          padding: {
            top: 2,
            right: 5,
            bottom: 1,
            left: 5
          },
          formatter: (value, context) => {
            const { dataIndex, dataset } = context;
            const isMatch = dataset.label === 'Match';
            const isUnmatch = dataset.label === 'Unmatch';

            const originalValue = isMatch
              ? this.originalMatch[dataIndex]
              : isUnmatch
              ? this.originalUnmatch[dataIndex]
              : null;

            if (originalValue === 0) {
              return null;
            }

            return value === originalValue ? value : `${originalValue}`;
          }
        }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true
      }
    },
  }

  constructor(private router: Router,
              private chartsVccService: ChartsVccService,
              private matchingVccService: MatchingVccService,
              private merchantsService: MerchantsService,
  ) {
  }

  ngOnInit() {
    this.isLoading = true;
    this.isError = false;

    const today = moment();
    this.chartsVccService.getLatestDate()
      .pipe(
        catchError(error => {
          console.error('Error getting latest date:', error);
          const fallbackDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
          return of(fallbackDate);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((latestReleasedDate) => {
        this.vccChartFilter.propertyName = 'releasedDateFrom';
        this.vccChartFilter.orderKey = 'DESC';
        this.vccChartFilter.releasedDateFrom = latestReleasedDate;
        this.vccChartFilter.releasedDateTo = today.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        this.chartsVccService.getVccChartFilter()
          .pipe(takeUntil(this.destroy$))
          .subscribe((filter) => {
            this.vccChartFilter.merchantGuid = filter.merchantGuid;
            this.vccChartFilter.releasedDateFrom = filter.releasedDateFrom ? moment(filter.releasedDateFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : this.vccChartFilter.releasedDateFrom;
            this.vccChartFilter.releasedDateTo = filter.releasedDateTo ? moment(filter.releasedDateTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : this.vccChartFilter.releasedDateTo;

            this.merchantsService.merchants$
              .pipe(takeUntil(this.destroy$))
              .subscribe((data)=>{
                this.merchantGuids = data;
                this.getMatchingData();
              },
              error => {
                console.error('Error getting merchants data:', error);
                this.isLoading = false;
                this.isError = true;
              });
          },
          error => {
            console.error('Error getting VCC chart filter:', error);
            this.getMatchingData();
          });
      },
      error => {
        console.error('Error in ngOnInit:', error);
        this.isLoading = false;
        this.isError = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get dateFilterText() {
    return this.chartsVccService.getDateSubTitle();
  }

  getMatchingData(): void {
    this.isLoading = true;

    this.chartsVccService.searchMatchingVcc(this.vccChartFilter)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
        catchError(error => {
          console.error('Error getting matching data:', error);
          this.isError = true;
          return of([]);
        })
      )
      .subscribe((data: MatchingResult[]) => {
        if (data && data.length > 0) {
          this.isError = false;
          this.matchingData = data;
          this.buildMatchingDataSet(data);
        } else {
          this.matchingData = [];
          this.dataMatching = {
            labels: [],
            datasets: [{
              data: [],
              label: 'Match',
              backgroundColor: '#0289C7'
            },
            {
              data: [],
              label: 'Unmatch',
              backgroundColor: '#00593F'
            }]
          };
        }
      });
  }

  buildMatchingDataSet(matchingData: MatchingResult[] ){
    this.shortLabels = matchingData.map(item => {
      const merchant = this.merchantGuids.find(m => m.merchantGuid === item.merchantGuid);
      return merchant ? merchant.shortName : '';
    });
    this.originalMatch = matchingData.map((item) => item.match);
    this.originalUnmatch = matchingData.map((item) => item.unmatch);

    const maxMatch = Math.max(...matchingData.map(item => item.match));
    this.limitPercentageMatch = maxMatch * this.baseLimit;
    const maxUnmatch = Math.max(...matchingData.map(item => item.unmatch));
    this.limitPercentageUnmatch = maxUnmatch * this.baseLimit;

    const adjustedMatch = matchingData.map((item: MatchingResult) => {
      this.adjustedValueMatch = item.match;
      if (this.adjustedValueMatch < this.limitPercentageMatch) {
        this.adjustedValueMatch += (this.limitPercentageMatch - this.adjustedValueMatch) * this.adjustedLimit;
      }
      return this.adjustedValueMatch;
    });

    const adjustedUnmatch = matchingData.map((item: MatchingResult) => {
      this.adjustedValueUnmatch = item.unmatch;
      if (this.adjustedValueUnmatch < this.limitPercentageUnmatch) {
        this.adjustedValueUnmatch += (this.limitPercentageUnmatch - this.adjustedValueUnmatch) * this.adjustedLimit;
      }
      return this.adjustedValueUnmatch;
    });

    this.dataMatching = {
      labels: this.shortLabels,
      datasets: [{
          data: adjustedMatch,
          label: 'Match',
          backgroundColor: '#0289C7'
        },
        {
          data: adjustedUnmatch,
          label: 'Unmatch',
          backgroundColor: '#00593F'
        }
      ]
    };
  }

  navigateToReport(event: ChartResponse) {
    const selectedMerchant = this.merchantGuids.find(merchant => merchant.shortName === event.label);
    this.matchingVccFilterConfigResult.merchantGuid = selectedMerchant?.merchantGuid;
    this.matchingVccFilterConfigResult.isAmountMatches = event.datasetIndex > 0 ? '0' : '1';
    this.matchingVccFilterConfigResult.releasedDateFrom = this.vccChartFilter.releasedDateFrom ? moment(this.vccChartFilter.releasedDateFrom).toDate() : null;
    this.matchingVccFilterConfigResult.releasedDateTo = this.vccChartFilter.releasedDateTo ? moment(this.vccChartFilter.releasedDateTo).toDate() : null;
    this.matchingVccFilterConfigResult.isMapped = null;
    this.matchingVccService.setMatchingFilter(this.matchingVccFilterConfigResult);
    this.router.navigate(['/FinanceResources/MatchingVcc']);
  }
}
